import { Cursor, Image, ImageType, ValidationResult } from "@mcleod/components";
import { Api, CompanySettings, Navigation, getLogger } from "@mcleod/core";
import { AutogenLayoutResetPassword } from "./autogen/AutogenLayoutResetPassword";

const log = getLogger("common.ResetPassword");
export class ResetPassword extends AutogenLayoutResetPassword {
    private _token: String;
    private _minLength: number;
    private _minSpecials: number;
    private _minUppers: number;
    private _minNumeric: number;
    private _specialAllowed: String;

    onLoad() {
        this._token = new URLSearchParams(window.location.search).get('resetToken');

        Api.post("validate-token", { token: this._token, check_user_key: true }).then(response => {
            if (response.data[0].success == true) {
                this.panelMain._element.parentElement.style.height = "100%";
                const settings = CompanySettings.get();
                this.panelContent.backgroundColor = "backgroundSubtle";
                if (settings.header_bg_color != null)
                    this.panelHeader.backgroundColor = settings.header_bg_color;
                if (settings.header_logo != null)
                    this.panelLogo.add(new Image({ imageType: ImageType.IMG, imageBytes: settings.header_logo, height: 60, width: "auto" }))
                this.imgRequirements.cursor = Cursor.POINTER;
                this.loadPasswordRequirements();
                this.setBackgroundImage();
            }
            else
                Navigation.navigateTo("common/Login");
        }).catch(reason => {
            Navigation.navigateTo("common/Login");
        });
    }

    setBackgroundImage() {
        const bytes = localStorage.getItem("loginBackground");
        if (bytes != null)
            this.panelPage.backgroundImageBytes = bytes;
        Api.search("lib/login-background", { cached_length: bytes?.length || -1 }).then(response => {
            const data = response?.login_background;
            if (data != null) {
                this.panelPage.backgroundImageBytes = data;
                localStorage.setItem("loginBackground", data);
            }
        });
    }

    getRouterProps() {
        return { padding: 0 };
    }

    buttonSaveClicked() {
        if (this.textPassword.text !== this.textConfirmPassword.text) {
            this.textPassword.validationWarning = "Passwords must match.";
            this.textConfirmPassword.validationWarning = "Passwords must match.";
        }
        else if (this.textPassword.text.length < this._minLength) {
            this.textPassword.validationWarning = "Password must have a length of at least " + this._minLength + " characters.";
            this.textConfirmPassword.validationWarning = "Password must have a length of at least " + this._minLength + " characters.";
        }
        else if (!this.meetsUpperCaseCount()) {
            this.textPassword.validationWarning = "Password must have at least " + this._minUppers + " upper case character(s).";
            this.textConfirmPassword.validationWarning = "Password must have at least " + this._minUppers + " upper case character(s).";
        }
        else if (!this.meetsNumericCount()) {
            this.textPassword.validationWarning = "Password must have at least " + this._minNumeric + " numeric character(s).";
            this.textConfirmPassword.validationWarning = "Password must have at least " + this._minNumeric + " numeric character(s).";
        }
        else if (!this.meetsSpecialCharCount()) {
            this.textPassword.validationWarning = "Password must have at least " + this._minSpecials + " special character(s). Special characters allowed are: " + this._specialAllowed;
            this.textConfirmPassword.validationWarning = "Password must have at least " + this._minSpecials + " special character(s). Special characters allowed are: " + this._specialAllowed;
        }
        else if (!this.isSpecialCharsValid()) {
            this.textPassword.validationWarning = "Password contains invalid special characters. Special characters allowed are: " + this._specialAllowed;
            this.textConfirmPassword.validationWarning = "Password contains invalid special characters. Special characters allowed are: " + this._specialAllowed;
        }
        else if (this.panelContent.validateSimple()) {
            this.buttonSave.busy = true;
            this.setPassword(this.textPassword.text);
        }
    }

    showPasswordCriteria() {
        if (this._minLength > 0) {
            this.labelMinimumLength.caption = "- Password must be at least " + this._minLength + " characters long";
            this.labelMinimumLength.visible = true;
        }
        if (this._minUppers > 0) {
            this.labelMinimumUppers.caption = "- Password must have at least " + this._minUppers + " upper case character(s)";
            this.labelMinimumUppers.visible = true;
        }
        if (this._minNumeric > 0) {
            this.labelMinimumNumeric.caption = "- Password must have at least " + this._minNumeric + " numeric character(s)";
            this.labelMinimumNumeric.visible = true;
        }
        if (this._minSpecials > 0) {
            this.labelMinimumSpecials.caption = "- Password must have at least " + this._minSpecials + " special character(s)";
            this.labelMinimumSpecials.visible = true;
        }
        if (this._specialAllowed) {
            this.labelAllowedSpecials.caption = "- Special characters allowed are: " + this._specialAllowed;
            this.labelAllowedSpecials.visible = true;
        }
    }

    buttonCancelClicked() {
        Navigation.navigateTo("");
    }

    loadPasswordRequirements() {
        Api.search("users/password/requirements", { appendPrefix: false }).then(response => {
            this._minLength = response.data[0].password_length;
            this._minSpecials = response.data[0].special_chars;
            this._minUppers = response.data[0].upper_chars;
            this._minNumeric = response.data[0].numeric_chars;
            this._specialAllowed = response.data[0].allowed_special_chars;
        }).catch(err => {
            log.error("Error", err)
        })
    }

    passwordLengthValidator(length: number): ValidationResult {
        if (this.textPassword.text.length < length)
            return { component: this.textPassword, isValid: false, validationMessage: "Password must have a length of at least " + length + " characters." };
    }

    meetsUpperCaseCount(): boolean {
        const str = this.textPassword.text;
        if (str.length > 0) {
            let count = 0;
            for (let i = 0; i < str.length; i++) {
                if (str[i] >= 'A' && str[i] <= 'Z') {
                    count++;
                }
            }
            if (count >= this._minUppers)
                return true;
        }
        return false;
    }

    meetsNumericCount(): boolean {
        const str = this.textPassword.text;
        if (str.length > 0) {
            let count = 0;
            for (let i = 0; i < str.length; i++) {
                if (str[i] >= '0' && str[i] <= '9') {
                    count++;
                }
            }
            if (count >= this._minNumeric)
                return true;
        }
        return false;
    }

    meetsSpecialCharCount(): boolean {
        const str = this.textPassword.text;
        if (str.length > 0) {
            const allowedSpecialChars = this._specialAllowed;
            let count = 0;
            for (let i = 0; i < str.length; i++) {
                if (allowedSpecialChars.includes(str[i])) {
                    count++;
                }
            }
            if (count >= this._minSpecials)
                return true;
        }
        return false;
    }

    isSpecialCharsValid(): boolean {
        const str = this.textPassword.text;
        if (str.length > 0) {
            const allowedSpecialChars = this._specialAllowed;
            for (let i = 0; i < str.length; i++) {
                const char = str[i];
                if (!(char >= 'A' && char <= 'Z') &&
                        !(char >= 'a' && char <= 'z') &&
                        !(char >= '0' && char <= '9') &&
                        !allowedSpecialChars.includes(char)) {
                    return false;
                }
            }
        }
        return true;
    }

    setPassword(password: string) {
        Api.post("user/password-reset", { password: password, token: this._token }, { appendPrefix: false }).then(response => {
            Navigation.navigateTo("common/Login");
        }).catch(err => {
            log.error("Error", err)
        })
    }

}
